import * as api from "@nens/threedi/openapi-client";

import { AppState, Thunk } from "../App";
import * as selectedLeveeEditRedux from "../state/existingLeveeEditsMenu";
import { getXY } from "../state/selectedPoint";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { getSelected } from "./existingRasterEditsMenu";
import { getLineDataStatus } from "./mapProfileLine";
import { getSelectedAsset } from "./selectedAsset";
import { RECEIVED_WEBSOCKET_MESSAGE, WebsocketMessageAction } from "./simulation";

export type Tool =
  | "DISCHARGE"
  | "PUMP"
  | "RAIN"
  | "WIND"
  | "RASTER_EDIT"
  | "LAYERS"
  | "EXPORT"
  | "LEVEE_TOOL"
  | "INFO";
export type SelectionType = "SELECTION" | "LINE_SELECTION";
export type LeftRibbonButtonType = SelectionType | Tool;
export type RightCollapsableSideBarItem = "ASSET" | "PROFILE" | "RASTEREDIT";

export type Level = "info" | "warning" | "error";

export type ActiveModal =
  | "NONE"
  | "QUIT_SIMULATION"
  | "RESTART_SIMULATION"
  | "LEAVE_SESSION"
  | "SAVE_RESULTS"
  | "SIMULATION_FINISHED"
  | "PREFERENCES";

export interface SnackbarMessage {
  id: number;
  message: JSX.Element | string;
  level: Level;
}

interface MainGuiState {
  activeTool: Tool | null;
  activeSelectionType: SelectionType | null;
  nextSnackbarId: number;
  snackbar: SnackbarMessage[];
  userMenuOpen: boolean;
  activeModal: ActiveModal;
  flashPlayButton: number;
}

const INITIAL_STATE: MainGuiState = {
  activeTool: null,
  activeSelectionType: "SELECTION",
  nextSnackbarId: 0,
  snackbar: [],
  userMenuOpen: false,
  activeModal: "NONE",
  flashPlayButton: 0, // Even numbers is off
};

export const getActiveTool = (state: AppState) => state.mainGui.activeTool;
export const getActiveSelectionType = (state: AppState) => state.mainGui.activeSelectionType;
export const getUserMenuOpen = (state: AppState) => state.mainGui.userMenuOpen;
export const getSnackbar = (state: AppState) => state.mainGui.snackbar;
export const getActiveModal = (state: AppState) => state.mainGui.activeModal;
export const getFlashPlayButton = (state: AppState) => state.mainGui.flashPlayButton;

export const getRightCollapsableSidebarVisible = (state: AppState) => {
  const activeTool = getActiveTool(state);
  const activeSelectionType = getActiveSelectionType(state);
  const lineDataStatus = getLineDataStatus(state);
  const selectedAsset = getSelectedAsset(state);
  const selectedRasterEdit = getSelected(state);
  const selectedLeveeEdit = selectedLeveeEditRedux.getSelected(state);
  const xy = getXY(state);
  const showProfileTool = activeSelectionType === "LINE_SELECTION" && lineDataStatus;
  const showSelection = selectedAsset && activeSelectionType === "SELECTION";
  const showRasterEdit = activeTool === "RASTER_EDIT" && selectedRasterEdit;
  const showLeveeEdit = selectedLeveeEdit;
  const showPointEdit = xy;

  return !!(showProfileTool || showSelection || showRasterEdit || showLeveeEdit || showPointEdit);
};

const SET_ACTIVE_TOOL = "mainGui/SET_ACTIVE_TOOL";
const SET_ACTIVE_TOOL_NONE = "mainGui/SET_ACTIVE_TOOL_NONE";
const SET_ACTIVE_SELECTION_TYPE = "mainGui/SET_ACTIVE_SELECTION_TYPE";
const SET_ACTIVE_SELECTION_TYPE_NONE = "mainGui/SET_ACTIVE_SELECTION_TYPE_NONE";
const ADD_TO_SNACKBAR = "mainGui/ADD_TO_SNACKBAR";
const REMOVE_FROM_SNACKBAR = "mainGui/REMOVE_FROM_SNACKBAR";
const SET_USER_MENU_OPEN = "mainGui/SET_USER_MENU_OPEN";
const SET_ACTIVE_MODAL = "mainGui/SET_ACTIVE_MODAL";
const INACTIVATE_MODAL = "mainGui/INACTIVATE_MODAL";
const START_FLASH_PLAY_BUTTON = "mainGui/START_FLASH_PLAY_BUTTON";
const CONTINUE_FLASH_PLAY_BUTTON = "mainGui/CONTINUE_FLASH_PLAY_BUTTON";
const STOP_FLASH_PLAY_BUTTON = "mainGui/STOP_FLASH_PLAY_BUTTON";

interface SetActiveTool {
  type: typeof SET_ACTIVE_TOOL;
  activeTool: Tool;
}
interface SetActiveToolNone {
  type: typeof SET_ACTIVE_TOOL_NONE;
}
interface SetActiveSelectionType {
  type: typeof SET_ACTIVE_SELECTION_TYPE;
  activeSelectionType: SelectionType;
}
interface SetActiveSelectionTypeNone {
  type: typeof SET_ACTIVE_SELECTION_TYPE_NONE;
}
interface SetUserMenuOpen {
  type: typeof SET_USER_MENU_OPEN;
  userMenuOpen: boolean;
}

interface AddToSnackbar {
  type: typeof ADD_TO_SNACKBAR;
  message: string;
  level?: Level;
}

interface RemoveFromSnackbar {
  type: typeof REMOVE_FROM_SNACKBAR;
  message: string;
}
interface SetActiveModal {
  type: typeof SET_ACTIVE_MODAL;
  activeModal: ActiveModal;
}
interface InactivateModal {
  type: typeof INACTIVATE_MODAL;
}

interface StartFlashPlayButton {
  type: typeof START_FLASH_PLAY_BUTTON;
}

interface ContinueFlashPlayButton {
  type: typeof CONTINUE_FLASH_PLAY_BUTTON;
}

interface StopFlashPlayButton {
  type: typeof STOP_FLASH_PLAY_BUTTON;
}

type MainGuiAction =
  | SetActiveTool
  | SetActiveToolNone
  | SetActiveSelectionType
  | SetActiveSelectionTypeNone
  | AddToSnackbar
  | RemoveFromSnackbar
  | SetUserMenuOpen
  | SetActiveModal
  | InactivateModal
  | BackToStartScreen
  | WebsocketMessageAction
  | StartFlashPlayButton
  | ContinueFlashPlayButton
  | StopFlashPlayButton;

export const setActiveTool = (activeTool: Tool) => {
  return {
    type: SET_ACTIVE_TOOL,
    activeTool: activeTool,
  };
};
export const setActiveToolNone = () => {
  return {
    type: SET_ACTIVE_TOOL_NONE,
  };
};
export const setActiveSelectionType = (activeSelectionType: SelectionType) => {
  return {
    type: SET_ACTIVE_SELECTION_TYPE,
    activeSelectionType,
  };
};
export const setActiveSelectionTypeNone = () => {
  return {
    type: SET_ACTIVE_SELECTION_TYPE_NONE,
  };
};
export const setUserMenuOpen = (userMenuOpen: boolean) => {
  return {
    type: SET_USER_MENU_OPEN,
    userMenuOpen: userMenuOpen,
  };
};

export const sendMessage =
  (message: string | JSX.Element, timeoutMs = 5000, level: Level = "info"): Thunk =>
  (dispatch) => {
    dispatch({
      type: ADD_TO_SNACKBAR,
      message,
      level,
    });

    window.setTimeout(() => {
      dispatch({
        type: REMOVE_FROM_SNACKBAR,
        message,
      });
    }, timeoutMs);
  };

export const setActiveModal = (activeModal: ActiveModal): SetActiveModal => {
  return {
    type: SET_ACTIVE_MODAL,
    activeModal: activeModal,
  };
};

export const inactivateModal = (): InactivateModal => {
  return {
    type: INACTIVATE_MODAL,
  };
};

export const remindUserToPressPlay = (): StartFlashPlayButton => {
  return {
    type: START_FLASH_PLAY_BUTTON,
  };
};

export const continueFlashPlayButton = (): ContinueFlashPlayButton => {
  return {
    type: CONTINUE_FLASH_PLAY_BUTTON,
  };
};

export const stopFlashPlayButton = (): StopFlashPlayButton => {
  return {
    type: STOP_FLASH_PLAY_BUTTON,
  };
};

export default function reducer(state = INITIAL_STATE, action: MainGuiAction): MainGuiState {
  switch (action.type) {
    case SET_ACTIVE_TOOL: {
      const { activeTool } = action;
      return {
        ...state,
        activeTool,
      };
    }
    case SET_ACTIVE_TOOL_NONE:
      return {
        ...state,
        activeTool: null,
      };
    case SET_ACTIVE_SELECTION_TYPE: {
      const { activeSelectionType } = action;
      return {
        ...state,
        activeSelectionType,
      };
    }
    case SET_ACTIVE_SELECTION_TYPE_NONE:
      return {
        ...state,
        activeSelectionType: null,
      };
    case ADD_TO_SNACKBAR:
      return {
        ...state,
        snackbar: state.snackbar.concat([
          {
            id: state.nextSnackbarId,
            message: action.message,
            level: action.level || "error",
          },
        ]),
        nextSnackbarId: state.nextSnackbarId + 1,
      };
    case REMOVE_FROM_SNACKBAR: {
      const index = state.snackbar.findIndex((message) => message.message === action.message);
      if (index !== -1) {
        // Copy snackbar and remove item
        const snackbar = state.snackbar.slice();
        snackbar.splice(index, 1);
        return {
          ...state,
          snackbar,
        };
      } else {
        return state;
      }
    }
    case SET_USER_MENU_OPEN: {
      const { userMenuOpen } = action;
      return {
        ...state,
        userMenuOpen,
      };
    }
    case SET_ACTIVE_MODAL: {
      const { activeModal } = action; //as SetActiveModal;
      return {
        ...state,
        activeModal,
      };
    }
    case RECEIVED_WEBSOCKET_MESSAGE: {
      if (action.payload && action.payload.type === "status") {
        const message = action.payload;
        const data = message.data;
        const status: api.SimulationStatus = data.data;
        if (status.name === "finished") {
          return {
            ...state,
            activeModal: "SIMULATION_FINISHED",
          };
        }
      }
      return state;
    }
    case INACTIVATE_MODAL:
      return {
        ...state,
        activeModal: "NONE",
      };
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case START_FLASH_PLAY_BUTTON:
      return {
        ...state,
        flashPlayButton: state.flashPlayButton === 0 ? 1 : state.flashPlayButton,
      };
    case STOP_FLASH_PLAY_BUTTON:
      return {
        ...state,
        flashPlayButton: 0,
      };
    case CONTINUE_FLASH_PLAY_BUTTON:
      return {
        ...state,
        flashPlayButton:
          state.flashPlayButton === 0
            ? 0
            : state.flashPlayButton >= 3
            ? 0
            : state.flashPlayButton + 1,
      };
    default:
      return state;
  }
}
